import React, { Component } from 'react';
import { Redirect } from 'react-router';

import { FetchResult } from '@apollo/client';
import { DataProxy } from '@apollo/client/cache';
import { MutationFunction } from '@apollo/client/react';
import { Mutation, Query } from '@apollo/client/react/components';
import { Box, Button, Card, Flex, H4, Loader, Text } from '@opendoor/bricks/core';
import ContentCard from '@opendoor/bricks/deprecated/UNSAFE_layout/ContentCard';
import { spaceWithUnits } from '@opendoor/bricks/theme/eero';

import { ContentWrapper } from '../../components/ContentWrapper';
import TestModeBanner from '../../components/TestModeBanner';
import { productionClient, testingClient } from '../../config/apolloClients';
import { TestModeContext } from '../../config/context';
import { GENERATE_API_KEY, IGenerateApiKey } from '../../graphql/mutations/GenerateApiKey';
import {
  IRevokeApiKey,
  IRevokeApiKeyVariables,
  REVOKE_API_KEY,
} from '../../graphql/mutations/RevokeApiKey';
import { GET_API_KEYS, IGetApiKeys } from '../../graphql/queries/GetApiKeys';
import { getErrorMessage } from '../../helpers/formatUtils';
import { ERROR_PAGE_ROUTE } from '../routes';

const MAX_KEYS = 2;

interface IKeyState {
  token: string;
  createdAt: string;
}

class APIKeys extends Component {
  public state: {
    productionKeyStates: Array<IKeyState>;
    stagingKeyStates?: Array<IKeyState>;
  } = {
    productionKeyStates: [],
  };

  public render() {
    return (
      <TestModeContext.Consumer>
        {(testMode) => (
          <ContentWrapper>
            <ContentCard
              title="API Keys"
              borderRadius="roundedSquare"
              paddingTop={testMode ? spaceWithUnits[10] : undefined}
            >
              {testMode ? <TestModeBanner /> : null}
              <Box mt={3}>
                Your API keys are unique and sensitive. Keep them safe and never check them into
                your codebase. It is recommended to store and access these from your deployment’s
                environment variables.
              </Box>
              <Box mt={2}>
                <a
                  target="_blank"
                  href="https://docs.opendoor.com/walkthroughs/make-your-first-request#adding-authorization-header"
                  rel="noopener noreferrer"
                >
                  Learn more →
                </a>
              </Box>
              <Query<IGetApiKeys>
                query={GET_API_KEYS}
                client={testMode ? testingClient : undefined}
              >
                {({ loading, error, data }) => {
                  if (error) {
                    return <Redirect to={ERROR_PAGE_ROUTE} />;
                  }
                  if (loading) {
                    return <Loader />;
                  }
                  return this.renderKeySection({
                    keyStates: data ? data.getPartnerAuthTokens : [],
                    testMode,
                  });
                }}
              </Query>
            </ContentCard>
          </ContentWrapper>
        )}
      </TestModeContext.Consumer>
    );
  }

  private renderKeySection({
    keyStates,
    testMode,
  }: {
    keyStates: Array<IKeyState>;
    testMode: boolean;
  }) {
    const maxedOut = keyStates.length >= MAX_KEYS;
    const heading = testMode ? 'Testing' : 'Production';
    const name = testMode ? 'test' : 'production';
    return (
      <Box mt={7}>
        <H4 color="neutrals100" mb={2}>
          {heading}
        </H4>
        <Box mb={5}>
          {keyStates.length > 0 ? (
            <Text>Here are your active {name} keys</Text>
          ) : (
            <Text>You don’t have a {name} key yet</Text>
          )}
        </Box>
        <Box mb={5}>
          <Box>
            {keyStates.map(({ token, createdAt }) =>
              this.renderKeyModule({ key: token, createdAt: new Date(createdAt), testMode }),
            )}
            {this.renderGenerateButton(maxedOut, testMode)}
            {maxedOut && (
              <Text mt={2}>
                We allow a maximum of {MAX_KEYS} active keys. Please revoke a key if you need to
                generate a new one.
              </Text>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  private renderKeyModule = ({
    key,
    createdAt,
    testMode,
  }: {
    key: string;
    createdAt: Date;
    testMode: boolean;
  }) => (
    <Mutation<IRevokeApiKey, IRevokeApiKeyVariables>
      mutation={REVOKE_API_KEY}
      update={this.handleRevokeUpdate(key)}
      key={key}
      client={testMode ? testingClient : productionClient}
    >
      {(revokeKey, { error }) => (
        <Flex key={key} mb={4} flexDirection="column">
          {error ? (
            <Text color="terracotta50">
              Failed to revoke API key.&nbsp;{getErrorMessage(error)}
            </Text>
          ) : null}
          <Flex>
            <Card
              px={3}
              py={2}
              border={2}
              borderRadius="roundedSquare"
              borderStyle="solid"
              borderColor="neutrals100"
              maxWidth="calc(100% - 120px)"
              overflowX="auto"
            >
              <Text color="neutrals100" fontSize="s00" fontWeight="bold" fontFamily="mono">
                {key}
              </Text>
            </Card>
            <Button
              fontSize="s00"
              ml={4}
              px={3}
              onClick={this.revokeKeyHandler(revokeKey, { variables: { token: key } })}
              style={{ cursor: 'pointer' }}
              width="fit-content"
              height="fit-content"
              variant="secondary"
              analyticsName="partnership-dashboard-fe-revoke-api-key"
              aria-label="Revoke API key"
            >
              Revoke
            </Button>
          </Flex>
          <Text mt={2} fontSize="s00" color="neutrals70">
            Created {createdAt.toLocaleDateString()} at {createdAt.toLocaleTimeString()}
          </Text>
        </Flex>
      )}
    </Mutation>
  );

  private renderGenerateButton = (maxedOut: boolean, testMode: boolean) => (
    <Mutation<IGenerateApiKey, unknown>
      mutation={GENERATE_API_KEY}
      update={this.handleGenerateUpdate}
      client={testMode ? testingClient : productionClient}
    >
      {(generateKey, { error }) => {
        return (
          <Box mt={2} display="inline-block">
            <Flex flexDirection="column">
              {error ? (
                <Text color="terracotta50">
                  Failed to generate API key.&nbsp;{getErrorMessage(error)}
                </Text>
              ) : null}
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <Button
                analyticsName="partnership-dashboard-fe-generate-api-key"
                aria-label=""
                variant="primary"
                onClick={this.generateKeyHandler(generateKey)}
                disabled={maxedOut}
              >
                Generate new API key
              </Button>
            </Flex>
          </Box>
        );
      }}
    </Mutation>
  );

  private revokeKeyHandler =
    (
      mutationFn: MutationFunction<IRevokeApiKey, IRevokeApiKeyVariables>,
      variables: Record<string, unknown>,
    ) =>
    (_: React.MouseEvent) => {
      if (
        window.confirm(
          'Revoking this key will cause any requests made with key to be denied. ' +
            'Make sure this key is no longer in use before revoking it.',
        )
      ) {
        mutationFn(variables);
      }
    };

  private generateKeyHandler =
    (mutationFn: MutationFunction<IGenerateApiKey, unknown>) => (_: React.MouseEvent) => {
      mutationFn();
    };

  private handleRevokeUpdate = (key: string) => (cache: DataProxy) => {
    const keys: any = cache.readQuery({ query: GET_API_KEYS });

    if (keys) {
      cache.writeQuery({
        query: GET_API_KEYS,
        data: {
          getPartnerAuthTokens: keys.getPartnerAuthTokens.filter(
            (keyObj: IKeyState) => keyObj.token !== key,
          ),
        },
      });
    }
  };

  private handleGenerateUpdate = (cache: DataProxy, { data }: FetchResult<IGenerateApiKey>) => {
    const keys: any = cache.readQuery({ query: GET_API_KEYS });

    if (keys && data?.createPartnerAuthToken) {
      cache.writeQuery({
        query: GET_API_KEYS,
        data: {
          getPartnerAuthTokens: keys.getPartnerAuthTokens.concat([
            { token: data.createPartnerAuthToken, createdAt: new Date(), __typename: '' },
          ]),
        },
      });
    }
  };
}

export default APIKeys;
